import {useEffect, useRef, useState, type ReactNode} from "react";
import {MenuCustomDropdown} from "@vesta/aergia/components/dropdown";
import {IconButton} from "@vesta/aergia/components/iconButton";
import AppSwitcherIcon from "@vesta/aergia/svgs/core/app-switcher.svg?react";
import {ButtonItem, Section} from "@vesta/aergia/components/menu";
import {type DataAnalyticsProps} from "@vesta/aergia/utilities/dataAnalytics";
import {useNavigate} from "@tanstack/react-router";

export const PageLayoutSwitchSites = ({
    appSwitcherConfig,
    selectedAppId,
}: {
    appSwitcherConfig: AppSwitcherConfig[];
    selectedAppId?: string;
}) => {
    const [selectedLeftId, setSelectedLeftId] = useState(selectedAppId ?? appSwitcherConfig[0]?.id);
    const closeRef = useRef<(() => void) | null>(null);
    const navigate = useNavigate();

    const renderMenuItem = (item: MenuItemConfig) => {
        if (item.type === "link") {
            return (
                <ButtonItem
                    key={item.id}
                    iconBefore={item.iconBefore}
                    iconAfter={item.iconAfter}
                    title={item.title}
                    description={item.description}
                    isSelected={item.isSelected}
                    className={item.className}
                    paddingInline={item.paddingInline}
                    dataAnalyticsEvent={item.dataAnalyticsEvent}
                    dataAnalyticsProps={item.dataAnalyticsProps}
                    onClick={() => {
                        if (item.inSeparateWindow === true) {
                            window.open(item.href, "_blank", "noopener,noreferrer");
                        } else {
                            void navigate({to: item.href});
                        }
                        closeRef.current?.();
                    }}
                />
            );
        }
        return (
            <ButtonItem
                key={item.id}
                iconBefore={item.iconBefore}
                iconAfter={item.iconAfter}
                title={item.title}
                description={item.description}
                isSelected={item.isSelected}
                className={item.className}
                paddingInline={item.paddingInline}
                dataAnalyticsEvent={item.dataAnalyticsEvent}
                dataAnalyticsProps={item.dataAnalyticsProps}
                onClick={() => {
                    item.onClick();
                    closeRef.current?.();
                }}
            />
        );
    };

    useEffect(() => {
        if (selectedAppId !== undefined && appSwitcherConfig.some((config) => config.id === selectedAppId)) {
            setSelectedLeftId(selectedAppId);
        }
    }, [appSwitcherConfig, selectedAppId]);

    const selectedConfig = appSwitcherConfig.find((c) => c.id === selectedLeftId) ?? appSwitcherConfig[0];
    return (
        <MenuCustomDropdown
            closeRef={closeRef}
            trigger={
                <IconButton
                    icon={(props) => <AppSwitcherIcon {...props} />}
                    appearance="subtle"
                    tooltip={{content: "Switch sites or apps"}}
                />
            }
        >
            <div
                className="min-h-[200px]"
                onClick={(event) => {
                    if ((event.target as Element).closest("[data-app-switcher-heading]") !== null) {
                        closeRef.current?.();
                    }
                }}
            >
                <div className="flex max-w-[100vw] h-[80vh] relative">
                    {/* <div className="absolute rounded-b-[5px] left-0 bottom-0 content-[''] h-[16px] w-full bg-linear-to-b from-ds-background-neutral-subtle to-ds-surface-overlay"></div> */}
                    <div className="flex flex-col border-ds-border border-r-ds-border-width border-solid` gap-ds-space-100 p-ds-space-150 overflow-y-auto">
                        {appSwitcherConfig.map((nav) => (
                            <ButtonItem
                                key={nav.id}
                                iconBefore={nav.icon}
                                isSelected={nav.id === selectedLeftId}
                                onClick={() => setSelectedLeftId(nav.id)}
                                paddingInline="px-ds-space-050"
                                paddingBlock="py-ds-space-050"
                            />
                        ))}
                    </div>
                    <div className="overflow-y-auto w-[343px] h-[calc(100%+16px)] text-ds-text">
                        <div className="min-h-[calc(100%-60px)]">
                            <div>
                                <div className="mx-ds-space-200 my-ds-space-150">
                                    <div data-app-switcher-heading>{selectedConfig?.heading}</div>
                                </div>
                                {selectedConfig?.sections.map((section) => (
                                    <Section
                                        key={section.id}
                                        title={section.title}
                                        hasSeparator={section.hasSeparator}
                                    >
                                        <div className="m-ds-space-150 mt-ds-space-0">{section.items.map(renderMenuItem)}</div>
                                    </Section>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MenuCustomDropdown>
    );
};

export type BaseMenuItem = {
    id: string;
    isSelected?: boolean;
    iconBefore?: (props: {className?: string}) => ReactNode;
    iconAfter?: (props: {className?: string}) => ReactNode;
    title?: string;
    description?: string;
    className?: string;
    paddingInline?: string;
} & DataAnalyticsProps;

export type LinkMenuItem = BaseMenuItem & {
    type: "link";
    href: string;
    inSeparateWindow?: boolean;
};

export type ButtonMenuItem = BaseMenuItem & {
    type: "button";
    onClick: () => void;
};

export type MenuItemConfig = LinkMenuItem | ButtonMenuItem;

export type MenuItemConfigInput =
    | (Omit<LinkMenuItem, keyof DataAnalyticsProps> & Partial<DataAnalyticsProps>)
    | (Omit<ButtonMenuItem, keyof DataAnalyticsProps> & Partial<DataAnalyticsProps>);

export function menuItemConfig(item: MenuItemConfigInput): MenuItemConfig {
    if (item.type === "link") {
        return {
            ...item,
            dataAnalyticsEvent: item.dataAnalyticsEvent === undefined ? null : item.dataAnalyticsEvent,
            dataAnalyticsProps: item.dataAnalyticsProps === undefined ? null : item.dataAnalyticsProps,
        };
    }
    return {
        ...item,
        dataAnalyticsEvent: item.dataAnalyticsEvent === undefined ? null : item.dataAnalyticsEvent,
        dataAnalyticsProps: item.dataAnalyticsProps === undefined ? null : item.dataAnalyticsProps,
    };
}

export function withMenuItemAnalytics(
    sections: Array<Omit<MenuSectionConfig, "items"> & {items: Array<MenuItemConfigInput>}>,
): Array<MenuSectionConfig> {
    return sections.map((section) => {
        return {
            id: section.id,
            title: section.title,
            hasSeparator: section.hasSeparator,
            items: section.items.map(menuItemConfig),
        };
    });
}

export type MenuSectionConfig = {
    title?: string;
    hasSeparator?: boolean;
    id: string;
    items: MenuItemConfig[];
};

export type AppSwitcherConfig = {
    id: string;
    icon: (props: {className?: string}) => ReactNode;
    heading: ReactNode;
    description?: string;
    sections: MenuSectionConfig[];
};
